import { ReactNode, useEffect, useState } from 'react'
import logo from '/images/logos/logo-sexagogo-redwhite.png'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import MyButton from '../components/buttons'
import { getUser } from '../../services/oneUser'
import LanguageSelector from '../components/LanguageSelector'
import DashboardMenu from './dashboardMenu'
import AuthService from '../../services/login'
import { Menu, X } from 'lucide-react'
import CookieManager from '../lib/CookiesManager'

type DashboardLayoutProps = {
  title: string
  children: ReactNode
}

function DashboardLayout({ title, children }: DashboardLayoutProps) {
  const { t } = useTranslation()
  const [error, setError] = useState('')
  const [userStatus, setUserStatus] = useState<string[]>([])
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const navigate = useNavigate()
  const [verifiedStatus, setVerifiedStatus] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchStatus = async () => {
      const userId = CookieManager.getUserId()
      const token = CookieManager.getToken()

      if (!userId || !token) {
        setError('session_expired')
        setIsLoading(false)
        return
      }

      try {
        const userData = await getUser(userId, token)
        setUserStatus(userData.user.status)
        setVerifiedStatus(userData.user.verifiedStatus)
        setIsLoading(false)
      } catch (error) {
        if (
          error instanceof Error &&
          error.message === 'authentication_expired'
        ) {
          setError('session_expired')
        } else {
          setError('error_fetching_data')
        }
        setIsLoading(false)
      }
    }

    fetchStatus()
  }, [])

  const handleLoginClick = () => {
    AuthService.logout()
    navigate('/login')
  }

  if (error) {
    return (
      <div className='flex items-center justify-center min-h-screen px-4'>
        <div className='w-full max-w-md space-y-4'>
          <div className='p-6 bg-redlight rounded-lg text-center shadow-lg'>
            <p className='text-anthracite font-medium'>
              {t(`errors.${error}`)}
            </p>
          </div>
          <div className='text-center'>
            <Link
              to='/login'
              onClick={handleLoginClick}
              className='inline-block'
            >
              <MyButton variant='redpink-anthracite' className='w-full'>
                {t('login')}
              </MyButton>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='flex bg-white'>
      {/* Mobile Sidebar - Only visible on mobile when open */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-1/2 transform transition-transform duration-300 ease-in-out lg:hidden ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className='min-h-screen flex flex-col w-full bg-anthracite'>
          <div className='p-4 border-b border-anthraciteLight'>
            <Link to='/dashboard'>
              <img src={logo} alt='logo' className='h-14 mx-auto' />
            </Link>
          </div>
          <div className='flex-grow text-white'>
            <DashboardMenu
              userStatus={userStatus}
              isLoading={!userStatus.length}
              verifiedStatus={verifiedStatus}
            />
          </div>
          <div className='p-4'>
            <Link to='/' className='block'>
              <button className='w-full py-2 px-4 text-white bg-redpink hover:bg-redpink/90 rounded-md transition-colors'>
                {t('dashboard.to-the-site')}
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar - Always visible on large screens */}
      <div className='hidden lg:flex min-h-screen flex-col w-72 xl:w-80 bg-anthracite fixed'>
        <div className='p-4 border-b border-anthraciteLight'>
          <Link to='/dashboard'>
            <img src={logo} alt='logo' className='h-14 mx-auto' />
          </Link>
        </div>
        <div className='text-white flex-grow'>
          <DashboardMenu
            userStatus={userStatus}
            isLoading={!userStatus.length}
            verifiedStatus={verifiedStatus}
          />
        </div>
        <div className='p-4'>
          <Link to='/' className='block'>
            <button className='w-full py-2 px-4 text-white bg-redpink hover:bg-redpink/90 rounded-md transition-colors'>
              {t('dashboard.to-the-site')}
            </button>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 lg:ml-72 xl:ml-80 w-full'>
        <div className='bg-redpink sticky top-0 z-40'>
          <div className='flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3'>
            <div className='flex items-center'>
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className='lg:hidden text-white p-1 sm:p-2 -ml-1 sm:-ml-2 hover:bg-redpink/80 rounded-md'
              >
                {isSidebarOpen ? (
                  <X className='w-5 h-5 sm:w-6 sm:h-6' />
                ) : (
                  <Menu className='w-5 h-5 sm:w-6 sm:h-6' />
                )}
              </button>
              <h1 className='text-white text-sm sm:text-lg font-medium ml-2'>
                {title}
              </h1>
            </div>
            <div className='flex items-center gap-2 bg-white rounded-lg'>
              <LanguageSelector />
            </div>
          </div>
        </div>

        <div className='py-2 px-2 sm:px-4'>{children}</div>
      </div>

      {/* Overlay when mobile sidebar is open */}
      {isSidebarOpen && (
        <div
          className='fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300'
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  )
}

export default DashboardLayout
